import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus, Users, MonitorSmartphone, Clock, Search, MoreHorizontal, Eye, Pencil, Trash2, ShieldCheck, ShieldOff } from "lucide-react";
import ViewToggle from "@/components/ViewToggle";
import { TenantGrid } from "@/components/tenants/TenantGrid";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTenants, useTenantDeletions } from "@/hooks/useTenants";
import { TENANT_STATUS_CONFIG, TenantStatus } from "@/types/tenant";
import { format } from "date-fns";
import { RequestDeletionDialog } from "@/components/tenants/RequestDeletionDialog";

export default function TenantsPage() {
  const { tenants, isLoading, licenseUsage, isLoadingLicenseUsage } = useTenants();
  const { deletions } = useTenantDeletions();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("tenantsViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "list";
  });

  const handleViewChange = (newView: "grid" | "list") => {
    setView(newView);
    localStorage.setItem("tenantsViewPreference", newView);
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || tenant.tenant_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const hasPendingDeletion = (tenantId: string) => {
    return deletions.some(d => d.customer_id === tenantId && d.status === 'pending');
  };

  const handleRequestDeletion = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setDeletionDialogOpen(true);
  };

  const renderStatusBadge = (status: TenantStatus) => {
    const config = TENANT_STATUS_CONFIG[status] || TENANT_STATUS_CONFIG.active;
    return (
      <Badge variant="secondary" className={`${config.bgColor} ${config.color} border-0`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className="px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Tenant Administration</h1>
            <p className="text-muted-foreground mt-1">
              Manage customer tenants and monitor license usage
            </p>
          </div>
          <Button asChild>
            <Link to="/admin/tenants/onboard">
              <Plus className="h-4 w-4 mr-2" />
              Onboard Customer
            </Link>
          </Button>
        </div>

      {/* License Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Devices</CardTitle>
            <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingLicenseUsage ? "..." : licenseUsage?.totalDevices || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Enrolled across all tenants
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tenants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingLicenseUsage ? "..." : licenseUsage?.activeTenants || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently active customers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Onboarding</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingLicenseUsage ? "..." : licenseUsage?.pendingTenants || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Tenants being provisioned
            </p>
          </CardContent>
        </Card>
      </div>

        {/* Tenants Table */}
        <Card disableHover>
        <CardHeader>
          <CardTitle>Customer Tenants</CardTitle>
          <CardDescription>
            All customers associated with your partner account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tenants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="creating">Creating</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
                <SelectItem value="migrated">Migrated</SelectItem>
              </SelectContent>
            </Select>
            <ViewToggle view={view} onViewChange={handleViewChange} />
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery || statusFilter !== "all" 
                ? "No tenants match your filters"
                : "No tenants found. Click 'Onboard Customer' to add your first tenant."}
            </div>
          ) : view === "grid" ? (
            <TenantGrid
              tenants={filteredTenants}
              hasPendingDeletion={hasPendingDeletion}
              onRequestDeletion={handleRequestDeletion}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Devices</TableHead>
                  <TableHead>Parent Managed</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {tenant.logo_url ? (
                          <img 
                            src={tenant.logo_url} 
                            alt={tenant.name} 
                            className="h-8 w-8 rounded-md object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{tenant.name}</div>
                          <div className="text-sm text-muted-foreground">{tenant.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {tenant.tenant_type || 'customer'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {renderStatusBadge(tenant.tenant_status)}
                        {hasPendingDeletion(tenant.id) && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                            Deletion Pending
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{tenant.device_count}</div>
                      {tenant.expected_device_count && tenant.expected_device_count > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Expected: {tenant.expected_device_count}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.can_parent_manage !== false ? (
                        <div className="flex items-center gap-1.5 text-emerald-600">
                          <ShieldCheck className="h-4 w-4" />
                          <span className="text-sm">Yes</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <ShieldOff className="h-4 w-4" />
                          <span className="text-sm">No</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.industry ? (
                        <span className="capitalize">{tenant.industry}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.created_at ? format(new Date(tenant.created_at), "MMM d, yyyy") : "-"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/tenants/${tenant.id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/tenants/${tenant.id}/edit`}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          {tenant.tenant_status !== 'deleted' && !hasPendingDeletion(tenant.id) && (
                            <DropdownMenuItem 
                              onClick={() => handleRequestDeletion(tenant.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Request Deletion
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        </Card>

        {/* Deletion Dialog */}
        <RequestDeletionDialog
          open={deletionDialogOpen}
          onOpenChange={setDeletionDialogOpen}
          tenantId={selectedTenantId}
          tenantName={tenants.find(t => t.id === selectedTenantId)?.name}
        />
      </div>
    </div>
  );
}
